import React from "react";
import { useGetGiftcardProductsQuery, GiftcardProduct } from "../../../features/giftcards/giftcardsSlice";

// Currency symbol helper
const getCurrencySymbol = (code: string): string => {
  const symbols: Record<string, string> = {
    USD: "$", EUR: "€", GBP: "£", NGN: "₦", CAD: "C$", AUD: "A$",
    JPY: "¥", CNY: "¥", INR: "₹", BRL: "R$", MXN: "$", ZAR: "R",
    KRW: "₩", THB: "฿", PHP: "₱", IDR: "Rp", MYR: "RM", SGD: "S$",
    HKD: "HK$", TWD: "NT$", AED: "د.إ", SAR: "﷼", PKR: "₨", BDT: "৳",
    VND: "₫", EGP: "E£", KES: "KSh", GHS: "GH₵", TZS: "TSh", UGX: "USh",
  };
  return symbols[code] || `${code} `;
};

interface ProductListProps {
  countryCode: string;
  brandId: number;
  onSelect: (product: GiftcardProduct) => void;
}

export const ProductList: React.FC<ProductListProps> = ({
  countryCode,
  brandId,
  onSelect,
}) => {
  const { data: products, isLoading, error } = useGetGiftcardProductsQuery({
    countryCode,
    brandId,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="h-32 bg-gray-800 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-4">
        Failed to load products. Please try again.
      </div>
    );
  }

  // Format recipient amount (gift card face value with local currency)
  const formatRecipientAmount = (product: GiftcardProduct) => {
    const symbol = getCurrencySymbol(product.recipientCurrencyCode);

    if (product.denominationType === "FIXED") {
      const amounts = product.fixedRecipientAmounts;
      if (!amounts || amounts.length === 0) return "N/A";
      if (amounts.length === 1) return `${symbol}${amounts[0].toLocaleString()}`;
      return `${symbol}${amounts[0].toLocaleString()} - ${symbol}${amounts[amounts.length - 1].toLocaleString()}`;
    }
    return `${symbol}${product.minRecipientAmount?.toLocaleString()} - ${symbol}${product.maxRecipientAmount?.toLocaleString()}`;
  };

  // Format sender cost (what user actually pays in USD)
  const formatSenderCost = (product: GiftcardProduct) => {
    const symbol = getCurrencySymbol(product.senderCurrencyCode);

    if (product.denominationType === "FIXED") {
      const amounts = product.fixedSenderAmounts;
      if (!amounts || amounts.length === 0) return "";
      if (amounts.length === 1) return `${symbol}${amounts[0].toLocaleString()}`;
      return `${symbol}${amounts[0].toLocaleString()} - ${symbol}${amounts[amounts.length - 1].toLocaleString()}`;
    }
    return `${symbol}${product.minSenderAmount?.toLocaleString()} - ${symbol}${product.maxSenderAmount?.toLocaleString()}`;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {products?.map((product) => (
        <button
          key={product.productId}
          onClick={() => onSelect(product)}
          className="flex items-start gap-4 p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-left"
        >
          <img
            src={product.logoUrls[0]}
            alt={product.productName}
            className="w-16 h-16 object-contain rounded"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://via.placeholder.com/64";
            }}
          />
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-medium truncate">{product.productName}</h4>
            <p className="text-primary text-sm mt-1">{formatRecipientAmount(product)}</p>
            <p className="text-gray-400 text-xs mt-1">
              Cost: {formatSenderCost(product)}
            </p>
            <p className="text-gray-500 text-xs mt-1">
              {product.denominationType === "FIXED" ? "Fixed amounts" : "Custom amount"}
              {product.senderFee > 0 && ` • +${getCurrencySymbol(product.senderCurrencyCode)}${product.senderFee.toFixed(2)} fee`}
            </p>
          </div>
        </button>
      ))}

      {products?.length === 0 && (
        <div className="col-span-2 text-gray-400 text-center py-8">
          No products available for this brand.
        </div>
      )}
    </div>
  );
};
